import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  ShieldCheck, Search, User, KeyRound, Loader2, Pencil, UserCheck, UserX, ArrowLeft
} from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { useUserManagement, ManagedUser } from "@/hooks/admin/useUserManagement";
import { useNavigate } from "react-router-dom";
import ActiveSessionsCard from "@/components/admin/ActiveSessionsCard";

const AdminGebruikersBeheer = () => {
  const { users, isLoading, updateUser, toggleActive, resetPassword } = useUserManagement();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  // Dialog states
  const [editUser, setEditUser] = useState<ManagedUser | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState<ManagedUser | null>(null);
  const [newPassword, setNewPassword] = useState("temp123");
  const [isResetting, setIsResetting] = useState(false);

  // Edit form state
  const [editRole, setEditRole] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [editFunctie, setEditFunctie] = useState("");
  const [editAfdeling, setEditAfdeling] = useState("");

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.naam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = companyFilter === "all" || (u.company || "luijten_vvz") === companyFilter;
    const matchesRole = roleFilter === "all" || u.functie === roleFilter || u.role === roleFilter;
    return matchesSearch && matchesCompany && matchesRole;
  });

  const isActive = (u: ManagedUser) => u.active !== false && u.is_active !== false;

  const openEditDialog = (user: ManagedUser) => {
    setEditUser(user);
    setEditRole(user.functie || user.role || "medewerker");
    setEditCompany(user.company || "luijten_vvz");
    setEditFunctie(user.functie || "");
    setEditAfdeling(user.afdeling || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editUser) return;
    const selectedRole = editFunctie || editRole || "medewerker";
    updateUser.mutate({
      userId: editUser.id,
      updates: {
        functie: selectedRole,
        role: selectedRole,
        company: editCompany,
        afdeling: editAfdeling,
      },
    }, {
      onSuccess: () => setEditDialogOpen(false),
    });
  };

  const openResetDialog = (user: ManagedUser) => {
    setResetUser(user);
    setNewPassword("temp123");
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    setIsResetting(true);
    const success = await resetPassword(resetUser.id, newPassword);
    setIsResetting(false);
    if (success) {
      setResetDialogOpen(false);
    }
  };

  const handleToggleActive = (user: ManagedUser) => {
    const currentlyActive = isActive(user);
    toggleActive.mutate({ userId: user.id, active: !currentlyActive });
  };


  const getRoleBadge = (user: ManagedUser) => {
    const role = user.functie || user.role || "medewerker";
    const variant = role === "management" ? "default" : role === "planner" ? "secondary" : "outline";
    return <Badge variant={variant}>{role}</Badge>;
  };

  const getCompanyBadge = (user: ManagedUser) => {
    const company = user.company || "luijten_vvz";
    return (
      <Badge variant="outline" className={company === "decoprof" ? "border-accent text-accent-foreground" : ""}>
        {company === "luijten_vvz" ? "Luijten VVZ" : "Decoprof"}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Terug
        </Button>
        <ShieldCheck className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Gebruikersbeheer</h1>
          <p className="text-muted-foreground">
            Beheer gebruikers, rollen, bedrijven en wachtwoorden
          </p>
        </div>
      </div>

      <ActiveSessionsCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Gebruikers ({filteredUsers.length})</span>
          </CardTitle>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 pt-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Zoek op naam, gebruikersnaam of e-mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Bedrijf" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle bedrijven</SelectItem>
                <SelectItem value="luijten_vvz">Luijten VVZ</SelectItem>
                <SelectItem value="decoprof">Decoprof</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle rollen</SelectItem>
                <SelectItem value="medewerker">Medewerker</SelectItem>
                <SelectItem value="planner">Planner</SelectItem>
                <SelectItem value="management">Management</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="border rounded-lg overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Naam</TableHead>
                    <TableHead>Gebruikersnaam</TableHead>
                    <TableHead>Bedrijf</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Laatste login</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        Geen gebruikers gevonden
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id} className={!isActive(user) ? "opacity-50" : ""}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {user.naam || "-"}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.username || "-"}
                        </TableCell>
                        <TableCell>{getCompanyBadge(user)}</TableCell>
                        <TableCell>{getRoleBadge(user)}</TableCell>
                        <TableCell>
                          <Badge variant={isActive(user) ? "default" : "destructive"}>
                            {isActive(user) ? "Actief" : "Inactief"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {user.last_login
                            ? format(new Date(user.last_login), "d MMM yyyy HH:mm", { locale: nl })
                            : "Nooit"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} title="Bewerken">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openResetDialog(user)} title="Wachtwoord resetten">
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleActive(user)}
                              title={isActive(user) ? "Deactiveren" : "Activeren"}
                            >
                              {isActive(user) ? (
                                <UserX className="h-4 w-4 text-destructive" />
                              ) : (
                                <UserCheck className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gebruiker bewerken</DialogTitle>
            <DialogDescription>
              Wijzig de gegevens van <strong>{editUser?.naam}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Functie / Rol</label>
              <Select value={editFunctie || editRole} onValueChange={(v) => { setEditFunctie(v); setEditRole(v); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medewerker">Medewerker</SelectItem>
                  <SelectItem value="planner">Planner</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Bedrijf</label>
              <Select value={editCompany} onValueChange={setEditCompany}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="luijten_vvz">Luijten VVZ</SelectItem>
                  <SelectItem value="decoprof">Decoprof</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Afdeling</label>
              <Input value={editAfdeling} onChange={(e) => setEditAfdeling(e.target.value)} placeholder="Bijv. Verkoop, Magazijn..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleSaveEdit} disabled={updateUser.isPending}>
              {updateUser.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wachtwoord resetten</DialogTitle>
            <DialogDescription>
              Reset het wachtwoord voor <strong>{resetUser?.naam}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nieuw wachtwoord</label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Voer nieuw wachtwoord in"
              />
              <p className="text-xs text-muted-foreground">
                De gebruiker moet dit wachtwoord gebruiken om in te loggen
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleResetPassword} disabled={!newPassword || isResetting}>
              {isResetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Wachtwoord resetten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGebruikersBeheer;
